import {
	amountToNat,
	getAnonymousAgent,
	getAuthenticatedActor,
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
} from "@/lib/protocol-actor";
import {
	createMmkBookActor,
	getMmkBookCanisterId,
	type MmkBookActor,
	type MmkBookSide,
} from "@/lib/mmk-book-actor";
import { getEnvMap } from "@/lib/network";
import { approveIcrcSpender } from "@motokomaker/ic-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Identity } from "@icp-sdk/core/agent";

function optNat(v: [] | [bigint] | undefined): number | null {
	if (!v || v.length === 0) return null;
	return natToAmount(v[0]);
}

async function getBookActor(agent?: Awaited<ReturnType<typeof getAnonymousAgent>>): Promise<MmkBookActor | null> {
	const id = getMmkBookCanisterId(getEnvMap());
	if (!id) return null;
	const ag = agent ?? (await getAnonymousAgent());
	if (!ag) return null;
	return createMmkBookActor(id, ag);
}

export function useMmkBookStatus() {
	return useQuery({
		queryKey: ["v2", "mmk-book", "status"],
		queryFn: async () => {
			const book = await getBookActor();
			if (!book) return { source: "missing" as const };
			const [st, active] = await Promise.all([
				book.getStatus(),
				book.isOrderBookActive().catch(() => false),
			]);
			return {
				source: "live" as const,
				enabled: st.enabled,
				active,
				activateBps: Number(st.activateBps),
				makerFeeBps: Number(st.makerFeeBps),
				takerFeeBps: Number(st.takerFeeBps),
				openOrders: Number(st.openOrders),
				tradeCount: Number(st.tradeCount),
				lastPrice: optNat(st.lastPriceE8),
				mid: optNat(st.midE8),
				settlementMode: st.settlementMode,
			};
		},
		staleTime: 15_000,
		refetchInterval: 30_000,
	});
}

export function useMmkBookDepth(levels = 12) {
	return useQuery({
		queryKey: ["v2", "mmk-book", "depth", levels],
		queryFn: async () => {
			const book = await getBookActor();
			if (!book) return null;
			const d = await book.getOrderBook(BigInt(levels));
			return {
				bids: d.bids.map(([p, s]) => ({
					price: natToAmount(p),
					size: natToAmount(s),
				})),
				asks: d.asks.map(([p, s]) => ({
					price: natToAmount(p),
					size: natToAmount(s),
				})),
				last: optNat(d.lastPriceE8),
				mid: optNat(d.midE8),
			};
		},
		staleTime: 10_000,
		refetchInterval: 15_000,
		enabled: Boolean(getMmkBookCanisterId(getEnvMap())),
	});
}

/** Primary only-up mark vs secondary book mid. */
export function useDualMarks() {
	return useQuery({
		queryKey: ["v2", "mmk-book", "dual-marks"],
		queryFn: async () => {
			const proto = hasProtocolEnv() ? await getV2ProtocolActor() : null;
			let soldBps = 0;
			let primaryPrice: number | null = null;
			let sold = 0;
			let cap = 0;
			if (proto) {
				try {
					const t = await proto.getTokenomicsStatus();
					sold = natToAmount(t.soldMmkE8);
					cap = natToAmount(t.capMmkE8);
					if (cap > 0) soldBps = Math.floor((sold / cap) * 10_000);
				} catch {
					/* optional */
				}
				try {
					// Prefer stability/MMK swap quote if present
					const s = await (
						proto as {
							getMmkSwapStatus?: () => Promise<{
								currentPriceE8?: bigint;
								priceE8?: bigint;
							}>;
						}
					).getMmkSwapStatus?.();
					const pe = s?.currentPriceE8 ?? s?.priceE8;
					if (pe != null) primaryPrice = natToAmount(pe);
				} catch {
					/* optional */
				}
			}
			const book = await getBookActor();
			let secondaryMid: number | null = null;
			let secondaryLast: number | null = null;
			if (book) {
				const st = await book.getStatus();
				secondaryMid = optNat(st.midE8);
				secondaryLast = optNat(st.lastPriceE8);
			}
			return {
				soldBps,
				sold,
				cap,
				primaryPrice,
				secondaryMid,
				secondaryLast,
			};
		},
		staleTime: 20_000,
	});
}

export function useMyMmkOrders(identity: Identity | null | undefined) {
	return useQuery({
		queryKey: ["v2", "mmk-book", "my-orders", identity?.getPrincipal().toText()],
		queryFn: async () => {
			if (!identity) return [];
			const auth = await getAuthenticatedActor(identity);
			if (!auth) return [];
			const id = getMmkBookCanisterId(getEnvMap());
			if (!id) return [];
			const book = createMmkBookActor(id, auth.agent);
			const orders = await book.getMyOrders();
			return orders.map((o) => ({
				id: o.id.toString(),
				side: "BuyMmk" in o.side ? ("buy" as const) : ("sell" as const),
				price: natToAmount(o.priceE8),
				size: natToAmount(o.sizeMmkE8),
				filled: natToAmount(o.filledMmkE8),
				status:
					"Open" in o.status
						? "open"
						: "Filled" in o.status
							? "filled"
							: "cancelled",
			}));
		},
		enabled: Boolean(identity && getMmkBookCanisterId(getEnvMap())),
		staleTime: 10_000,
	});
}

export function usePlaceLimit(identity: Identity | null | undefined) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args: {
			side: "buy" | "sell";
			price: number;
			size: number;
		}) => {
			if (!identity) throw new Error("Connect Internet Identity");
			const auth = await getAuthenticatedActor(identity);
			if (!auth) throw new Error("Auth actor failed");
			const bookId = getMmkBookCanisterId(getEnvMap());
			if (!bookId) throw new Error("mmk_book canister ID not in env");
			const book = createMmkBookActor(bookId, auth.agent);
			const priceE8 = amountToNat(args.price);
			const sizeE8 = amountToNat(args.size);
			const side: MmkBookSide =
				args.side === "buy" ? { BuyMmk: null } : { SellMmk: null };

			// Settlement mode: approve book as spender
			const st = await book.getStatus();
			if (st.settlementMode) {
				const ledgers = await auth.actor.getLedgerCanisters();
				const usdmFromLedger = ledgers.usdm[0];
				const mmkFromLedger = ledgers.mmk[0];
				const usdmFromBook = st.usdmLedger[0];
				const mmkFromBook = st.mmkLedger[0];
				const usdmId = (usdmFromLedger ?? usdmFromBook)?.toText() ?? "";
				const mmkId = (mmkFromLedger ?? mmkFromBook)?.toText() ?? "";
				if (args.side === "buy") {
					if (!usdmId) throw new Error("USDM ledger missing");
					await approveIcrcSpender(
						auth.agent,
						usdmId,
						bookId,
						args.price * args.size,
					);
				} else {
					if (!mmkId) throw new Error("MMK ledger missing");
					await approveIcrcSpender(auth.agent, mmkId, bookId, args.size);
				}
			}

			const res = await book.placeLimit(side, priceE8, sizeE8);
			if ("err" in res) throw new Error(res.err);
			return res.ok.toString();
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["v2", "mmk-book"] });
			void qc.invalidateQueries({ queryKey: ["v2", "balances"] });
		},
	});
}

export function useCancelOrder(identity: Identity | null | undefined) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (orderId: string) => {
			if (!identity) throw new Error("Connect Internet Identity");
			const auth = await getAuthenticatedActor(identity);
			if (!auth) throw new Error("Auth actor failed");
			const bookId = getMmkBookCanisterId(getEnvMap());
			if (!bookId) throw new Error("mmk_book canister ID not in env");
			const book = createMmkBookActor(bookId, auth.agent);
			const res = await book.cancelOrder(BigInt(orderId));
			if ("err" in res) throw new Error(res.err);
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["v2", "mmk-book"] });
		},
	});
}

/** Recent fills — matching happens on counterparty place (no separate fulfill API). */
export function useMmkBookTrades(limit = 20) {
	return useQuery({
		queryKey: ["v2", "mmk-book", "trades", limit],
		queryFn: async () => {
			const book = await getBookActor();
			if (!book) return { total: 0, trades: [] as Array<{
				id: string;
				price: number;
				size: number;
				tsMs: number;
			}> };
			const page = await book.getTrades(0n, BigInt(limit));
			return {
				total: Number(page.total),
				trades: page.trades.map((t) => ({
					id: t.id.toString(),
					price: natToAmount(t.priceE8),
					size: natToAmount(t.sizeMmkE8),
					tsMs: Number(t.tsNs / 1_000_000n),
				})),
			};
		},
		staleTime: 12_000,
		refetchInterval: 20_000,
		enabled: Boolean(getMmkBookCanisterId(getEnvMap())),
	});
}
